// The running tour, kept outside React so the sidebar entry and the runner
// (mounted as a root element) can share it without a context provider.
// Progress is saved in localStorage so a reload resumes the tour; that is a
// per-visitor convenience, so every access tolerates storage being unavailable.

export type TourState = {
  /** Id of the running tour, or null when the chooser is open. */
  tourId: string | null;
  step: number;
};

const STATE_KEY = 'showroom.tour.state';
const SEEN_KEY = 'showroom.tour.seen';

type Listener = (state: TourState | undefined) => void;
const listeners = new Set<Listener>();

function read(): TourState | undefined {
  try {
    const raw = window.localStorage.getItem(STATE_KEY);
    return raw ? (JSON.parse(raw) as TourState) : undefined;
  } catch {
    return undefined;
  }
}

let current: TourState | undefined = read();

function set(state: TourState | undefined) {
  current = state;
  try {
    if (state) {
      window.localStorage.setItem(STATE_KEY, JSON.stringify(state));
    } else {
      window.localStorage.removeItem(STATE_KEY);
    }
  } catch {
    // Storage blocked: the tour still runs, it just won't survive a reload.
  }
  listeners.forEach(l => l(state));
}

export const tourStore = {
  get: () => current,
  subscribe(listener: Listener) {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  },
  start: (tourId: string) => set({ tourId, step: 0 }),
  goTo: (step: number) => current && set({ ...current, step }),
  openChooser: () => set({ tourId: null, step: 0 }),
  close: () => set(undefined),
  /** True once the visitor has been offered a tour, started or not. */
  seen(): boolean {
    try {
      return window.localStorage.getItem(SEEN_KEY) === '1';
    } catch {
      // Without storage every page load would restart the tour, so act as seen.
      return true;
    }
  },
  markSeen() {
    try {
      window.localStorage.setItem(SEEN_KEY, '1');
    } catch {
      // ignore
    }
  },
};
